import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { Checkbox } from '@/components/core/Checkbox';
import { DirectiveProps, ReactBridgeDirectiveWrapper } from '@/components/ReactBridgeDirectiveWrapper';
import { useTranslation } from '@/hooks/useTranslation';

import { GalleryMode, ItemAttribute } from '../export-media-asset.types';

interface MediaAttributeProps {
    attribute: ItemAttribute;
    displayAttributes?: string[];
    onAssetSelect: (item: ItemAttribute) => void;
    galleryMode: GalleryMode
}

const MediaAttribute = ({ attribute, displayAttributes, onAssetSelect, galleryMode }: MediaAttributeProps) => {

    const { t } = useTranslation();
    const [ isSelected, setIsSelected ] = useState(false);

    const getBadgeClassName = (type: 'doc' | 'image') => {
        if (type === 'doc') {
            return 'tw-bg-red-500';
        } else {
            return 'tw-bg-blue-500';
        }
    };

    useEffect(() => {
        setIsSelected(attribute.__selected);
    }, [attribute]);

    // To prevent the directive from re-rendering when its props haven't changed, we have
    // to memoize its props.
    const fileThumbnailProps = useMemo<DirectiveProps>(() => ({
        url: {
            value: attribute.__asset,
            type: '='
        },
        imgClass: {
            value: 'tw-w-full tw-h-full tw-object-contain tw-object-center tw-border-none',
            type: '@'
        },
        iconClass: {
            value: 'tw-text-4xl',
            type: '@'
        }
    }), [attribute.__asset]);

    return (
        <div onClick={() => onAssetSelect(attribute)}
            className={`tw-relative tw-flex tw-flex-col tw-shadow tw-mx-3 tw-my-3 tw-cursor-pointer
                ${ galleryMode === GalleryMode.SMALL ? 'tw-w-[200px] tw-min-h-[200px] tw-max-h-[200px]' : 'tw-w-[400px] tw-min-h-[400px] tw-max-h-[400px]'}`}>

            <div className={`tw-absolute tw-top-0 tw-left-0 tw-h-full tw-w-full tw-bg-light-primary ${isSelected ? 'tw-block': 'tw-hidden'}`}></div>

            <Badge  className={`tw-absolute tw-top-1 tw-right-1 tw-z-10 ${getBadgeClassName(attribute.__type)}`}>
                {attribute.__type === 'image' ? t('MEDIA_ASSET_EXPORT.IMAGE') : t('MEDIA_ASSET_EXPORT.DOCUMENT')}
            </Badge>

            <Checkbox isChecked={isSelected} className="tw-absolute tw-top-1 tw-left-1 tw-z-10" />

            <div className={`tw-flex-1 tw-h-3/4 tw-w-full hover:tw-opacity-100 ${isSelected ? 'tw-opacity-100' : 'tw-opacity-60 '}`}>
                <ReactBridgeDirectiveWrapper
                    className="tw-h-full tw-w-full"
                    name="file-thumbnail"
                    props={fileThumbnailProps} />
            </div>

            <div className="tw-flex tw-justify-center tw-truncate">
                <p>{displayAttributes?.map(attr => attribute[attr])}</p>
            </div>
        </div>
    );
};

export { MediaAttribute };
